import { useCallback, useState } from 'react';

import type { CreatedPayoutDraftData } from 'api';

import { CancelPayoutDraftButton } from './CancelPayoutDraftButton';
import { DraftProviders } from './contexts/DraftProviders';
import { CreateDraft } from './CreateDraft';
import { CreateManualPayoutFlow } from './CreateManualPayoutFlow';
import { FakeTopBar } from './FakeTopBar';
import { ManualPayoutSubmitted } from './ManualPayoutSubmitted';
import { ManualPayoutSubmittedToReview } from './ManualPayoutSubmittedToReview';
import type { SubmittedPayout } from './utils/types';

export const CreateManualPayout = () => {
  const [payoutDraft, setPayoutDraft] = useState<CreatedPayoutDraftData>();
  const [submittedPayout, setSubmittedPayout] = useState<
    SubmittedPayout | undefined
  >();

  const handleDraftCanceled = useCallback(() => {
    setPayoutDraft(undefined);
    setSubmittedPayout(undefined);
  }, []);

  if (submittedPayout) {
    if (submittedPayout.state === 'received') {
      return <ManualPayoutSubmitted submittedPayout={submittedPayout} />;
    }
    if (submittedPayout.state === 'waiting_review') {
      return (
        <ManualPayoutSubmittedToReview submittedPayout={submittedPayout} />
      );
    }
  }

  return (
    <div className="relative m-4 rounded-2xl shadow-lg">
      <div className="absolute left-0 top-0 size-full overflow-hidden rounded-2xl">
        <FakeTopBar height={16} />

        {/* Visual circles */}
        {payoutDraft && (
          <>
            <div className="absolute -right-20 top-44 size-56 rounded-full bg-highlight-muted"></div>
            <div className="absolute -bottom-10 right-1/3 size-56 rounded-full bg-tenant-subtle"></div>
          </>
        )}
      </div>
      {/* Card */}
      <div className="z-10 flex h-full flex-col space-y-10 px-12 py-8">
        <div className="flex justify-between">
          <div>
            <h1 className="text-2xl font-medium">Create payout</h1>
            <h3 className="font-light">
              Manually create a payout to a customer
            </h3>
          </div>
          {payoutDraft?.draft_id && (
            <CancelPayoutDraftButton
              draftId={payoutDraft.draft_id}
              onCancel={handleDraftCanceled}
            />
          )}
        </div>
        {payoutDraft === undefined ? (
          <CreateDraft setPayoutDraft={setPayoutDraft} />
        ) : (
          <DraftProviders fieldDefinitions={payoutDraft}>
            <CreateManualPayoutFlow onSubmitted={setSubmittedPayout} />
          </DraftProviders>
        )}
      </div>
    </div>
  );
};
